import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NavigationService } from '../../services/navigation/navigation.service';
import { CacheService } from '../../services/cache/cache.service';
import { CommonService } from '../../services/common/common.service';
import { Column } from '../interfaces/column.interface';
import moment from 'moment';
import { SportsDataService } from './sports-data.service';
import { Sport, SportsResult } from '../interfaces';
import { AgeRangesDataService } from './age-ranges-data.service';
import { AgeRange, AgeRangesResult } from '../interfaces';
import { IntensitiesDataService } from './intensities-data.service';
import { Intensity, IntensitiesResult } from '../interfaces';
import { ClassDurationsDataService } from './class-durations-data.service';
import { ClassDuration, ClassDurationsResult } from '../interfaces';
import { UsersDataService } from './users-data.service';
import { User, UsersResult } from '../interfaces';
import { FitnessGoalsDataService } from './fitness-goals-data.service';
import { FitnessGoal, FitnessGoalsResult } from '../interfaces';

@Injectable({
  providedIn: 'root'
})
export class FitnessClassesColumnsService {

  public sports: Sport[] = [];
  public ageRanges: AgeRange[] = [];
  public intensities: Intensity[] = [];
  public classDurations: ClassDuration[] = [];
  public users: User[] = [];
  public fitnessGoals: FitnessGoal[] = [];
  public fitnessGoalsFiltered: FitnessGoal[] = [];
  public defaultParameters: {[param: string]: string | string[]} = {offset: '0', limit: '1000'};

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private cache: CacheService,
    private common: CommonService,
    private sportsDataService: SportsDataService,
    private ageRangesDataService: AgeRangesDataService,
    private intensitiesDataService: IntensitiesDataService,
    private classDurationsDataService: ClassDurationsDataService,
    private usersDataService: UsersDataService,
    private fitnessGoalsDataService: FitnessGoalsDataService,

  ) {
    this.getSports()
    this.getAgeRanges()
    this.getIntensities()
    this.getClassDurations()
    this.getUsers()
    this.getFitnessGoals()
  }

  getSports(offset: number = 0): void {
    const cacheKey: string = 'sports.cache'
    const cache: any = this.cache.get(cacheKey);
    if (cache !== null && offset === 0) {
      this.sports = cache.results
      return;
    }
    const parameters = Object.assign(this.defaultParameters, {offset: offset + ''});
    this.sportsDataService.getSports(parameters).subscribe((response: SportsResult|any) => {
      if (response !== null) {
        if (offset === 0) {
          this.sports = response.results;
        } else {
          this.sports = this.sports.concat(response.results);
        }
        if (response.inlineCount > this.sports.length) {
          offset = offset + parseInt(this.defaultParameters.limit + '', 10);
          this.getSports(offset);
        }
        response.results = this.sports;
        this.cache.set(cacheKey, response);
      }
    });
  }

  getAgeRanges(offset: number = 0): void {
    const cacheKey: string = 'ageRanges.cache'
    const cache: any = this.cache.get(cacheKey);
    if (cache !== null && offset === 0) {
      this.ageRanges = cache.results
      return;
    }
    const parameters = Object.assign(this.defaultParameters, {offset: offset + ''});
    this.ageRangesDataService.getAgeRanges(parameters).subscribe((response: AgeRangesResult|any) => {
      if (response !== null) {
        if (offset === 0) {
          this.ageRanges = response.results;
        } else {
          this.ageRanges = this.ageRanges.concat(response.results);
        }
        if (response.inlineCount > this.ageRanges.length) {
          offset = offset + parseInt(this.defaultParameters.limit + '', 10);
          this.getAgeRanges(offset);
        }
        response.results = this.ageRanges;
        this.cache.set(cacheKey, response);
      }
    });
  }

  getIntensities(offset: number = 0): void {
    const cacheKey: string = 'intensities.cache'
    const cache: any = this.cache.get(cacheKey);
    if (cache !== null && offset === 0) {
      this.intensities = cache.results
      return;
    }
    const parameters = Object.assign(this.defaultParameters, {offset: offset + ''});
    this.intensitiesDataService.getIntensities(parameters).subscribe((response: IntensitiesResult|any) => {
      if (response !== null) {
        if (offset === 0) {
          this.intensities = response.results;
        } else {
          this.intensities = this.intensities.concat(response.results);
        }
        if (response.inlineCount > this.intensities.length) {
          offset = offset + parseInt(this.defaultParameters.limit + '', 10);
          this.getIntensities(offset);
        }
        response.results = this.intensities;
        this.cache.set(cacheKey, response);
      }
    });
  }

  getClassDurations(offset: number = 0): void {
    const cacheKey: string = 'classDurations.cache'
    const cache: any = this.cache.get(cacheKey);
    if (cache !== null && offset === 0) {
      this.classDurations = cache.results
      return;
    }
    const parameters = Object.assign(this.defaultParameters, {offset: offset + ''});
    this.classDurationsDataService.getClassDurations(parameters).subscribe((response: ClassDurationsResult|any) => {
      if (response !== null) {
        if (offset === 0) {
          this.classDurations = response.results;
        } else {
          this.classDurations = this.classDurations.concat(response.results);
        }
        if (response.inlineCount > this.classDurations.length) {
          offset = offset + parseInt(this.defaultParameters.limit + '', 10);
          this.getClassDurations(offset);
        }
        response.results = this.classDurations;
        this.cache.set(cacheKey, response);
      }
    });
  }

  getUsers(offset: number = 0): void {
    const cacheKey: string = 'users.cache'
    const cache: any = this.cache.get(cacheKey);
    if (cache !== null && offset === 0) {
      this.users = cache.results
      return;
    }
    const parameters = Object.assign(this.defaultParameters, {offset: offset + ''});
    this.usersDataService.getUsers(parameters).subscribe((response: UsersResult|any) => {
      if (response !== null) {
        if (offset === 0) {
          this.users = response.results;
        } else {
          this.users = this.users.concat(response.results);
        }
        if (response.inlineCount > this.users.length) {
          offset = offset + parseInt(this.defaultParameters.limit + '', 10);
          this.getUsers(offset);
        }
        response.results = this.users;
        this.cache.set(cacheKey, response);
      }
    });
  }

  getFitnessGoals(): void {
    const cacheKey: string = 'fitnessGoals.cache'
    const cache: any = this.cache.get(cacheKey);
    if (cache !== null) {
      this.fitnessGoals = cache.results
      return;
    }
    this.fitnessGoalsDataService.getFitnessGoals(this.defaultParameters).subscribe((response: FitnessGoalsResult|any) => {
      if (response !== null) {
        this.fitnessGoals = response.results;
        this.fitnessGoalsFiltered = response.results;
        this.cache.set(cacheKey, response);
      }
    });
  }

  filterFitnessGoals(searchText: string) {
    if (!searchText) {
      this.fitnessGoalsFiltered = Object.assign([], this.fitnessGoals);
      return;
    }
    this.fitnessGoalsFiltered = []
    this.fitnessGoals.map(
      (item: any) => {
        if (item.name.toLowerCase().indexOf(searchText.toLowerCase()) > -1) {
          this.fitnessGoalsFiltered.push(item);
        }
      }
    );
  }

  getColumns(): Column[] {
    return [
      { field: 'id', sortField: 'id', title: this.translate.instant('ID'), show: (this.navigation.getParamValue('fitnessClasses.id.showFiled', true) && true), displayInList: true, type: 'numericValue'},
      { field: 'name', sortField: 'name', title: this.translate.instant('NAME'), show: (this.navigation.getParamValue('fitnessClasses.name.showFiled', true) && true), displayInList: true, type: 'textValue'},
      { field: 'nameAr', sortField: 'nameAr', title: this.translate.instant('NAMEAR'), show: (this.navigation.getParamValue('fitnessClasses.nameAr.showFiled', true) && true), displayInList: true, type: 'textValue'},
      { field: 'nameFr', sortField: 'nameFr', title: this.translate.instant('NAMEFR'), show: (this.navigation.getParamValue('fitnessClasses.nameFr.showFiled', true) && true), displayInList: true, type: 'textValue'},
      { field: 'picture', sortField: 'picture', title: this.translate.instant('PICTURE'), show: (this.navigation.getParamValue('fitnessClasses.picture.showFiled', true) && true), displayInList: true, type: 'imgValue'},
      { field: 'mobilePicture', sortField: 'mobilePicture', title: this.translate.instant('MOBILEPICTURE'), show: (this.navigation.getParamValue('fitnessClasses.mobilePicture.showFiled', true) && true), displayInList: true, type: 'imgValue'},
      { field: 'title', sortField: 'title', title: this.translate.instant('TITLE'), show: (this.navigation.getParamValue('fitnessClasses.title.showFiled', true) && true), displayInList: true, type: 'textValue'},
      { field: 'titleAr', sortField: 'titleAr', title: this.translate.instant('TITLEAR'), show: (this.navigation.getParamValue('fitnessClasses.titleAr.showFiled', false) && true), displayInList: true, type: 'textValue'},
      { field: 'titleFr', sortField: 'titleFr', title: this.translate.instant('TITLEFR'), show: (this.navigation.getParamValue('fitnessClasses.titleFr.showFiled', false) && true), displayInList: true, type: 'textValue'},
      { field: 'content', sortField: 'content', title: this.translate.instant('CONTENT'), show: (this.navigation.getParamValue('fitnessClasses.content.showFiled', false) && true), displayInList: true, type: 'textValue'},
      { field: 'contentAr', sortField: 'contentAr', title: this.translate.instant('CONTENTAR'), show: (this.navigation.getParamValue('fitnessClasses.contentAr.showFiled', false) && true), displayInList: true, type: 'textValue'},
      { field: 'contentFr', sortField: 'contentFr', title: this.translate.instant('CONTENTFR'), show: (this.navigation.getParamValue('fitnessClasses.contentFr.showFiled', false) && true), displayInList: true, type: 'textValue'},
      { field: 'icon', sortField: 'icon', title: this.translate.instant('ICON'), show: (this.navigation.getParamValue('fitnessClasses.icon.showFiled', false) && true), displayInList: true, type: 'imgValue'},
      { field: 'color', sortField: 'color', title: this.translate.instant('COLOR'), show: (this.navigation.getParamValue('fitnessClasses.color.showFiled', false) && true), displayInList: true, type: 'colorValue'},
      { field: 'sport', title: this.translate.instant('SPORT'), type: 'linkValue', filterData: this.sports, show: (this.navigation.getParamValue('fitnessClasses.sport.showFiled', false) && true), displayInList: true, filterField: 'sport.id', displayField: 'name', sortField: 'sport.name', url: 'sports' },
      { field: 'ageRange', title: this.translate.instant('AGERANGE'), type: 'linkValue', filterData: this.ageRanges, show: (this.navigation.getParamValue('fitnessClasses.ageRange.showFiled', false) && true), displayInList: true, filterField: 'ageRange.id', displayField: 'name', sortField: 'ageRange.name', url: 'age-ranges' },
      { field: 'intensity', title: this.translate.instant('INTENSITY'), type: 'linkValue', filterData: this.intensities, show: (this.navigation.getParamValue('fitnessClasses.intensity.showFiled', false) && true), displayInList: true, filterField: 'intensity.id', displayField: 'name', sortField: 'intensity.name', url: 'intensities' },
      { field: 'classDuration', title: this.translate.instant('CLASSDURATION'), type: 'linkValue', filterData: this.classDurations, show: (this.navigation.getParamValue('fitnessClasses.classDuration.showFiled', false) && true), displayInList: true, filterField: 'classDuration.id', displayField: 'name', sortField: 'classDuration.name', url: 'class-durations' },
      { field: 'isPublished', sortField: 'isPublished', title: this.translate.instant('ISPUBLISHED'), show: (this.navigation.getParamValue('fitnessClasses.isPublished.showFiled', false) && true), displayInList: true, type: 'booleanValue', filterData: this.navigation.booleanOptions },
      { field: 'createdAt', sortField: 'createdAt', title: this.translate.instant('CREATEDAT'), show: (this.navigation.getParamValue('fitnessClasses.createdAt.showFiled', false) && true), displayInList: true, type: 'dateValue', valueFormatter: this.translate.instant('DATETIMEFORMAT')},
      { field: 'creatorUser', title: this.translate.instant('CREATORUSER'), type: 'linkValue', filterData: this.users, show: (this.navigation.getParamValue('fitnessClasses.creatorUser.showFiled', false) && true), displayInList: true, filterField: 'creatorUser.id', displayField: 'username', sortField: 'creatorUser.username', url: 'users' },
      { field: 'modifiedAt', sortField: 'modifiedAt', title: this.translate.instant('MODIFIEDAT'), show: (this.navigation.getParamValue('fitnessClasses.modifiedAt.showFiled', false) && true), displayInList: true, type: 'dateValue', valueFormatter: this.translate.instant('DATETIMEFORMAT')},
      { field: 'modifierUser', title: this.translate.instant('MODIFIERUSER'), type: 'linkValue', filterData: this.users, show: (this.navigation.getParamValue('fitnessClasses.modifierUser.showFiled', false) && true), displayInList: true, filterField: 'modifierUser.id', displayField: 'username', sortField: 'modifierUser.username', url: 'users' },
      { field: 'fitnessGoals', title: this.translate.instant('FITNESSGOALS'), type: 'linksValue', filterData: this.fitnessGoals, show: (this.navigation.getParamValue('fitnessClasses.fitnessGoals.showFiled', false) && true), displayInList: true, display: false, filterField: 'fitnessGoals.id', displayField: 'name', sortField: 'fitnessGoals[0].name', url: 'fitness-goals' },
      { field: 'actions', title: this.translate.instant('ACTIONS'), type: 'static', show: true, displayInList: true, sortField: null},
    ];
  }

  format(fieldName: string, value: any) {
    if (fieldName.toLowerCase().indexOf('time') > -1) {
      return moment(value).format('YYYY-MM-DD HH:mm');
    } else if (fieldName.toLowerCase().indexOf('date') > -1) {
      return moment(value).format('YYYY-MM-DD');
    }
    return value;
  }

}
